import type { RefObject } from 'react'

import { toError } from '@moment/observability/logger'

import { MomentEnv } from '@/app/moment-env'
import { createLogger } from '@/lib/logger'
import { getTodayStartHwm } from '@/utils/dates/dateHelpers'

const LOGGER = createLogger('utils/trade-blotter/web-socket')
export const RETRY_TIMEOUT_MS = 2000

export function updateHwmInLocalStorage(msgNumber: string, hwmKey: string) {
  const currentHwm = getCurrentHwm(hwmKey) || BigInt(getTodayStartHwm())
  // Increment HWM now that we successfully processed this event
  const eventMsgNumber = BigInt(msgNumber || 0) + 1n
  if (eventMsgNumber > currentHwm) {
    localStorage.setItem(
      `${window.location.pathname}/${hwmKey}`,
      eventMsgNumber.toString(),
    )
  }
}

/** Global variable to track the state of each active WebSocket connection */
const websockets: Record<
  string,
  { ws: WebSocket; id: number; attempts: number; isCleanedUp: boolean }
> = {}

async function constructWsUrl(
  hwm: bigint,
  route: string,
  time?: string,
): Promise<string | null> {
  try {
    const baseUrl = MomentEnv.ApiUrl?.replace('http', 'ws')
    if (!baseUrl) {
      throw new Error('API URL not configured')
    }

    let url = `${baseUrl}/${route}/?start=${hwm}`
    if (time) {
      url += `&start_time=${encodeURIComponent(time)}`
    }

    const response = await fetch('/api/auth/token', { method: 'POST' })
    if (!response.ok) {
      throw new Error('Failed to fetch auth token for WebSocket connection')
    }
    const { token } = await response.json()
    if (!token) {
      throw new Error('No token returned from auth endpoint')
    }
    url += `&auth_jwt=${encodeURIComponent(token)}`

    return url
  } catch (error) {
    LOGGER.error('Failed to construct WebSocket URL', {
      route,
      error: toError(error),
    })
    return null
  }
}

export async function setupNewWebSocket(
  hwm: bigint,
  route: string,
  messageEventListener: (event: MessageEvent) => void,
  openEventListener: () => void,
  closeEventListener: (event: CloseEvent) => void,
  time?: string,
): Promise<boolean> {
  const wsUrl = await constructWsUrl(hwm, route, time)
  if (!wsUrl) {
    LOGGER.error('Failed to setup WebSocket - invalid URL', {
      error: new Error('Failed to setup WebSocket - invalid URL'),
      route,
    })
    return false
  }

  const wsId = websockets[route]?.id || 0
  const attempts = (websockets[route]?.attempts || 0) + 1
  websockets[route] = {
    ws: new WebSocket(wsUrl),
    id: wsId + 1,
    attempts,
    isCleanedUp: false,
  }

  LOGGER.info('Attempting WebSocket connection', {
    route,
    attempts,
    wsId: wsId + 1,
  })
  const ws = websockets[route].ws

  ws.addEventListener('open', openEventListener)
  ws.addEventListener('message', messageEventListener)
  ws.addEventListener('close', closeEventListener)

  ws.addEventListener('message', (event: MessageEvent) => {
    LOGGER.debug('WebSocket message', {
      route,
      wsId: wsId + 1,
      data: event.data,
    })
  })
  ws.addEventListener('open', () => {
    LOGGER.info('WebSocket opened', { route, wsId: wsId + 1 })
  })
  ws.addEventListener('close', (event: CloseEvent) => {
    LOGGER.info('WebSocket closed', {
      route,
      wsId: wsId + 1,
      code: event.code,
      reason: event.reason,
    })
  })
  ws.addEventListener('error', () => {
    LOGGER.warn('WebSocket error', { route, wsId: wsId + 1, attempts })
  })

  return true
}

function getCurrentHwm(hwmKey: string): bigint | null {
  const path = window.location.pathname
  const hwm = localStorage.getItem(`${path}/${hwmKey}`)
  return hwm ? BigInt(hwm) : null
}

type AgGridCallback<T> = ({
  toAdd,
  toUpdate,
  toRemove,
}: {
  toAdd?: T[]
  toUpdate?: T[]
  toRemove?: T[]
}) => void

export function manageWebSocketLifecycle<T>(
  hwm: bigint,
  hwmKey: string,
  route: string,
  handleMessage: (
    event: MessageEvent,
    refToUpdate: RefObject<Map<string, T>>,
    callback: AgGridCallback<T>,
    shouldRemove?: (view: T) => boolean,
  ) => void,
  refToUpdate: RefObject<Map<string, T>>,
  callback?: AgGridCallback<T>,
  shouldRemove?: (view: T) => boolean,
  time?: string,
): () => void {
  const openEventListener = () => {
    const ws = websockets[route]
    if (!ws) return

    LOGGER.info(`WebSocket opened with id ${ws.id} for route ${route}`)
  }

  const messageEventListener = (event: MessageEvent) => {
    handleMessage(event, refToUpdate, callback || (() => {}), shouldRemove)
  }

  const closeEventListener = (event: CloseEvent) => {
    const ws = websockets[route]
    if (!ws) return

    LOGGER.info(`WebSocket closed with id ${ws.id} for route ${route}`, {
      code: event.code,
      reason: event.reason,
      wasClean: event.wasClean,
      attempts: ws.attempts,
    })

    // Don't reconnect if cleanup has been called
    if (ws.isCleanedUp) {
      LOGGER.info(`Skipping reconnection for route ${route} - cleaned up`)
      return
    }

    // Add delay before reconnecting
    setTimeout(async () => {
      const currentWs = websockets[route]
      // Check again in case cleanup happened during timeout
      if (!currentWs || currentWs.isCleanedUp) {
        LOGGER.info(
          `Skipping reconnection for route ${route} - cleaned up during timeout`,
        )
        return
      }

      try {
        const success = await setupNewWebSocket(
          getCurrentHwm(hwmKey) || BigInt(getTodayStartHwm()),
          route,
          messageEventListener,
          openEventListener,
          closeEventListener,
          time,
        )
        if (!success) {
          LOGGER.error('Failed to reconnect WebSocket', {
            error: new Error('Failed to reconnect WebSocket'),
            route,
            attempts: websockets[route]?.attempts,
          })
        }
      } catch (error) {
        LOGGER.error('Error reconnecting WebSocket', {
          route,
          attempts: websockets[route]?.attempts,
          error: toError(error),
        })
      }
    }, RETRY_TIMEOUT_MS)
  }

  setupNewWebSocket(
    hwm,
    route,
    messageEventListener,
    openEventListener,
    closeEventListener,
    time,
  ).catch((error) => {
    LOGGER.error('Failed initial WebSocket setup', {
      route,
      error: toError(error),
    })
  })

  return () => {
    if (websockets[route]) {
      websockets[route].isCleanedUp = true
      if (websockets[route].ws) {
        websockets[route].ws.removeEventListener(
          'message',
          messageEventListener,
        )
        websockets[route].ws.removeEventListener('open', openEventListener)
        websockets[route].ws.removeEventListener('close', closeEventListener)
        websockets[route].ws.close()
      }
      // Don't delete websockets[route] - preserve attempt counter for potential remounts/reconnections
    }
  }
}
